import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import HeaderBar from "../../../components/Header/HeaderBar";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import LoadingIcon from "../../../components/Loaders/LoadingIcon";
import ContentWrapper from "../../../components/ContentWrapper";
import AuthLabeledTextInput from "../../../components/Inputs/AuthLabeledTextInput";
import SubtleButton from "../../../components/Buttons/SubtleButton";
import { useSelector } from "react-redux";
import { ReduxState } from "../../../store/storeTypes";
import { Company } from "@mysnowscape/snow-lib";
import Tabs from "../../../components/Navigation/Tabs";
import RadioOption from "../../../components/Inputs/RadioOption";
import axios from "../../../services/axios/backendAxios";
import flash from "../../../services/flash";
import errors from "../../../services/errors";
import useAccountInfo from "../../../hooks/useAccountInfo";

export default function ShareJobsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const accountQuery = useAccountInfo();
  const [activeTab, setActiveTab] = useState<"mine" | "other">("mine");
  const [shareWithCompanyId, setShareWithCompanyId] = useState<string | null>(
    null,
  );
  const hasValidCompanyId =
    typeof shareWithCompanyId === "string" && shareWithCompanyId.length >= 24;
  const currentUser = useSelector((state: ReduxState) => state.currentUser);
  const currentCompanyId = useSelector(
    (state: ReduxState) => state.currentUser.currentCompanyId,
  );
  const jobIds: string[] = (params.get("jobIds")?.split(",") ?? []).filter(
    (s) => s.length > 0,
  );
  if (jobIds.length < 1) {
    throw new Error("Must supply at least 1 job id");
  }
  const companyQuery = useQuery<{ userCompanies: Company[] }>({
    queryKey: [`user/${currentUser.uid}/companies`],
  });
  const shareWithCompanyQuery = useQuery<{ name: string }>({
    queryKey: [`company/${shareWithCompanyId}/name`],
    enabled: hasValidCompanyId,
  });
  const companiesFiltered = companyQuery.data?.userCompanies.filter(
    (c) => c._id.toString() !== currentCompanyId,
  );
  const shareJobMutation = useMutation({
    mutationFn: async () => {
      await axios.post("job/share", {
        shareeCompanyId: shareWithCompanyId,
        sharerCompanyId: currentCompanyId,
        jobIds,
      });
    },
    onSuccess: () => {
      flash.success("Success!  These properties are now shared.");
      queryClient.invalidateQueries({
        queryKey: ["jobs"],
      });
      if (jobIds.length === 1) {
        navigate(`/admin/property/${encodeURIComponent(jobIds[0])}`);
      } else {
        navigate(`/admin/property`);
      }
    },
    onError: (err) => {
      errors.report(err);
      flash.error("Error: " + (err.message ?? err));
    },
    retry: 3,
  });
  const isLoading = companyQuery.isLoading || accountQuery.isLoading;
  return (
    <div className="flex flex-col items-center">
      <HeaderBar title={"Share Properties"} />
      <ContentWrapper loading={false} className="">
        {isLoading ? (
          <LoadingIcon />
        ) : (
          <>
            {(accountQuery.data?.account.usage.sharedProperties ?? 0) >=
            (accountQuery.data?.account.allowances.sharedProperties ?? 0) ? (
              <>
                <p className="text-gray-700 mb-6">
                  Sharing properties with sub-contractors makes it easy to
                  collaborate.
                </p>
                <p className="text-gray-700 mb-6">
                  You need to{" "}
                  <Link className="underline" to="/admin/settings/billing">
                    upgrade your plan
                  </Link>{" "}
                  to share more properties.
                </p>
              </>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!hasValidCompanyId || shareJobMutation.isPending) return;
                  shareJobMutation.mutate();
                }}
              >
                <p className="text-gray-700 mb-6">
                  Do you want to share these {jobIds.length} properties with a
                  sub-contractor? You can share these properties with a company
                  you control or another company that also uses SnowScape.
                  Sub-contractors will be able to see property details, service
                  the property, and view service reports for services they
                  performed. Sub-contractors will NOT be able to edit property
                  details (other than internal notes for their own company) or
                  view services records performed by yourself or other
                  companies.
                </p>

                <Tabs
                  tabs={[
                    { id: "mine", name: "Your Companies" },
                    { id: "other", name: "Other Company" },
                  ]}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
                <section className="mt-4">
                  {activeTab === "mine" ? (
                    <>
                      <ul className="">
                        {companiesFiltered?.map((c) => (
                          <li key={c._id}>
                            {/* @ts-expect-error: Radio option is not typed yet */}
                            <RadioOption
                              checked={
                                shareWithCompanyId?.toString() ===
                                c._id.toString()
                              }
                              onChange={() =>
                                setShareWithCompanyId(c._id.toString())
                              }
                            >
                              {c.name}
                            </RadioOption>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-700">
                        To share with another company you will need to know
                        their company id. You can find your company id under{" "}
                        <Link
                          to="/admin/settings/company"
                          className="hover:cursor-pointer underline"
                        >
                          Settings / Account &gt; Company
                        </Link>
                      </p>
                      <AuthLabeledTextInput
                        list="my-companies"
                        label="Company ID"
                        setValue={(v) => setShareWithCompanyId(v)}
                        value={shareWithCompanyId ?? ""}
                      />
                    </>
                  )}
                </section>

                <SubtleButton
                  type="submit"
                  buttonRole="primary"
                  disabled={!hasValidCompanyId || shareJobMutation.isPending}
                >
                  {hasValidCompanyId
                    ? `Share with ${shareWithCompanyQuery.data?.name ?? shareWithCompanyId ?? ""}`
                    : "Share"}
                </SubtleButton>
              </form>
            )}
          </>
        )}
      </ContentWrapper>
    </div>
  );
}
